import { App, Events, User } from "app-interfaces";
import { addMinutes } from "date-fns";
import React from "react";

import { useStateValue } from "../../config/AppContext";
import { Action, EventProps } from "../../types/StaticTypes";
import { ConcreteKey } from "../../types/utils";
import { formatDate, ofTimeOnlyWithZ } from "../../utils/time-utils";
import { LoadingModal, TimePickerModal } from "../Modals";
import ScheduleAvailability from "../Schedules/ScheduleAvailability";
import { ScheduleCalendar } from "../Schedules/ScheduleCalendar";
import { ScheduleState } from "../Schedules/SchedulesListings";

type MapSchedule = (el: Events.ISchedule) => EventProps;
type DashboardResponse = {
  events: Events.IAgenda[];
  start: string;
  end: string;
  event_name: string;
};
type SessionAvailabilityProps = {
  event: ConcreteKey<Events.IAgenda, "session">;
  session?: Events.SessionFull | null;
  dispatch: React.Dispatch<{ type: string; payload?: any }>;
  resetView: (start: any) => void;
  [x: string]: any;
};

export default function SessionAvailability(props: SessionAvailabilityProps) {
  const {
    settings,
    Methods: { getSessionAvailability, getScheduledEvents },
  } = useStateValue() as { settings: User.IUserSettings; Methods: App.Methods };
  const mapSchedule: MapSchedule = (el) => ({
    title: el.name,
    start: el.start,
    end: el.end,
    allDay: el.is_blocked,
    resource: {
      available: el.is_available,
      full: el.is_full,
      timezone: el.timezone,
      created: el.created,
    },
  });
  const [loading, setLoading] = React.useState<boolean>(false);
  const [sessionTime, setSessionTime] = React.useState<{
    datetime: Date;
    start: string;
    end: string;
  }>();

  const ScheduleReducer: React.Reducer<ScheduleState, Action> = (state, { type, payload }) => {
    const refreshSchedule = async (newPayload: any) => {
      const newSchedule = await getScheduledEvents({
        date: formatDate(new Date(newPayload.start), 9),
      });
      const dashEvents = await getSessionAvailability({
        date: formatDate(new Date(newPayload.start), 3),
        event_id: props.event.id,
      });
      if (!dashEvents) return setState({ type: "refresh", payload: state });
      const { events, start, end } = dashEvents;
      Object.assign(state, {
        events: newSchedule.map(mapSchedule),
        agenda: events,
        availability: { start, end },
      });
      return setState({ type: "refresh", payload: state });
    };
    const refreshDashboard = (props: DashboardResponse) => {
      if (!props) return state;
      const { events, start, end, event_name } = props;
      if (event_name.length > 0) {
        Object.assign(state, {
          event: {
            allDay: true,
            title: event_name,
          },
          agenda: events,
          availability: { start, end },
        });
      } else {
        Object.assign(state, {
          event: events.find(matchDate),
          agenda: events,
          availability: { start, end },
        });
      }
      return setState({ type: "refresh", payload: state });
    };
    switch (type) {
      case "refresh":
        if (payload && loading) {
          state = payload;
          setLoading(false);
        }
        break;
      case "schedule":
        if (payload && !loading) {
          setLoading(true);
          refreshSchedule(payload);
        }
        break;
      case "dashboard":
        if (payload && !loading) {
          setLoading(true);
          getSessionAvailability({
            date: formatDate(payload.start, 3),
            event_id: props.event.id,
          }).then(refreshDashboard);
        }
        break;
      case "reschedule":
        if (payload) {
          const newStart = addMinutes(new Date(payload.ev_start_time), settings.session_interval)
            .toLocaleString()
            .replace(" AM", "")
            .replace(" PM", "")
            .split(", ")[1];
          const newEnd = addMinutes(
            new Date(payload.ev_start_time),
            settings.session_interval + settings.session_length
          )
            .toLocaleString()
            .replace(" AM", "")
            .replace(" PM", "")
            .split(", ")[1];
          const checkPM = () => {
            const testDate = addMinutes(
              new Date(payload.ev_start_time),
              settings.session_interval
            ).toLocaleString();
            return /PM/.test(testDate);
          };

          // TODO: replace this madness of string manipulation with Luxon
          setSessionTime({
            datetime: payload.ev_start_time.split(" ")[0],
            start: ofTimeOnlyWithZ(
              checkPM()
                ? `${parseInt(newStart.split(":")[0]) + 12}:${parseInt(newStart.split(":")[1])}Z`
                : `${parseInt(newStart.split(":")[0])}:${parseInt(newStart.split(":")[1])}Z`
            ),
            end: ofTimeOnlyWithZ(
              checkPM()
                ? `${parseInt(newEnd.split(":")[0]) + 12}:${parseInt(newEnd.split(":")[1])}Z`
                : `${parseInt(newEnd.split(":")[0])}:${parseInt(newEnd.split(":")[1])}Z`
            ),
          });
        }
        break;
      default:
        throw new Error("Invalid action type");
    }

    /**
     * Callback function to find event with matching start date and payload start date
     */
    function matchDate(params: Events.IAgenda) {
      if (payload.event)
        return (
          new Date(params.ev_start_time.split(" ")[0]).getDate() ===
          new Date(payload.event.ev_start_time.split(" ")[0]).getDate()
        );
      return (
        new Date(params.ev_start_time.split(" ")[0]).getDate() ===
        new Date(payload.start.split("T")[0]).getDate()
      );
    }

    return state;
  };
  const [state, setState] = React.useReducer(ScheduleReducer, {
    events: [],
    agenda: [],
    availability: { start: "08:00Z", end: "08:00Z" },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!sessionTime) return;
    setLoading(true);
    const formData = new FormData(e.currentTarget);
    let payload: any = {};
    formData.forEach((v, k) => Object.assign(payload, { [k]: `${sessionTime.datetime}T${v}Z` }));
    const updatedEvent = {
      ...props.event,
      start: payload.from,
      end: payload.to,
      session: {
        id: props.event.session.id,
        customer_id: props.event.session.customer.map((el) => el.id),
        is_booked: props.event.session.is_booked,
        notes: props.event.session.notes,
        workout_id: props.session ? props.session?.workout?.id : undefined,
        state: props.event.session.state,
        state_notes: props.event.session.state_notes,
        state_reason: props.event.session.state_reason,
        state_time: props.event.session.state_time,
      },
      created: formatDate(new Date(), 6),
    };
    props.dispatch({
      type: "update-session",
      payload: { params: updatedEvent, callback: props.resetView },
    });
    return setSessionTime(undefined);
  };

  React.useEffect(() => {
    if (state.events.length === 0 && state.agenda.length === 0) {
      return setState({
        type: "schedule",
        payload: { start: formatDate(new Date(), 3) },
      });
    }
  }, [state]);

  return (
    <>
      <ScheduleCalendar events={state.events} dispatch={setState} />
      <hr />
      <ScheduleAvailability {...state} session={props.event.session} dispatch={setState} />
      {sessionTime && (
        <TimePickerModal
          show={!!sessionTime}
          currentItem={sessionTime}
          onHide={() => setSessionTime(undefined)}
          onSubmit={handleSubmit}
        />
      )}
      <LoadingModal show={loading} />
    </>
  );
}
